import { useMemo } from 'react'

import { useGetMarketplaceProducts } from 'components/hooks/useGetMarketplaceProducts'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { MarketplaceCards } from 'slab'

import { Product } from './Product'
import { type MarketplaceProductCategory } from '../shared/types'
import { isProductNew } from '../shared/utils'

const MAX_NEW_PRODUCTS = 3

export const MarketplaceProducts = ({ activeCategory }: { activeCategory: MarketplaceProductCategory }) => {
  const { data: products, error, isLoading, isSuccess } = useGetMarketplaceProducts()

  const displayProducts = useMemo(() => {
    if (!products) return []

    const filtered = products.data.filter((product) =>
      activeCategory === 'All' || product.categories.includes(activeCategory)
    )

    const newProducts = filtered
      .filter((p) => isProductNew(p.most_recent_variant_created_at))
      .sort((a, b) => a.rank - b.rank)

    const topNew = newProducts.slice(0, MAX_NEW_PRODUCTS)
    const topNewIds = new Set(topNew.map((p) => p.id))

    const remaining = filtered
      .filter((p) => !topNewIds.has(p.id))
      .sort((a, b) => a.rank - b.rank)

    return [...topNew, ...remaining]
  }, [products, activeCategory])

  if (isLoading && error === null) {
    return (
      <MarketplaceCards>
        {Array.from({ length: 6 }).map((_, index) => (
          <SkeletonItem key={index} heightClass='h-80' />
        ))}
      </MarketplaceCards>
    )
  }

  if (error) {
    // TODO(yousef): add better error representation
    return <div>Error: {error.message}</div>
  }

  return (
    <MarketplaceCards>
      {isSuccess &&
        displayProducts.map((product, index) => (
          <Product
            key={product.id}
            product={product}
            position={index + 1}
            activeCategory={activeCategory}
          />
        ))}
    </MarketplaceCards>
  )
}
